/**
 * VaultFileViewer — one window per Vault file, with two modes.
 *
 *   View  — a full rendered preview of the file, using the SAME renderer as the
 *           rest of the product (FilePreviewContent). Every type the system
 *           already previews works here: PDF, images, Word, Excel, Markdown,
 *           plain text.
 *   Edit  — in-place editing. Text-backed formats are editable today; other
 *           types show an honest "coming soon" notice instead of a dead box.
 *
 * Governance (mirrors backend VaultEditService):
 *   - PDF / Office        → not editable in place (view only, for now)
 *   - locked file         → read-only regardless of type
 *   - text formats        → editable, saved via PUT content
 * Managers can also set management_mode (managed/locked) and the agent
 * edit_approval_mode (ask/auto/never).
 */

import { useEffect, useState, useRef } from 'react';
import { Modal, Button, Spin, Alert, Select, Segmented, Space, Tooltip, Typography, message } from 'antd';
import { useLanguage } from '@/hooks/useLanguage';
import VaultSummarySheet from './VaultSummarySheet';
import api from '@services/api';
import { FilePreviewContent, getPreviewKind } from '@/components/common/FilePreviewContent';
import { VaultSheetEditor } from './VaultSheetEditor';
import { VaultDocEditor } from './VaultDocEditor';
import { VaultDeckEditor } from './VaultDeckEditor';
import { DeckPreview } from '@/components/common/DeckPreview';
import ExactPagesView from '@/components/common/ExactPagesView';
import { DeckDesignEditor, type DeckDesignHandle } from '@/components/common/DeckDesignEditor';
import { vaultService } from '../vaultService';
import { VaultDriveCopyBadge } from './VaultDriveCopyBadge';
import type { VaultFile, VaultFileContent, VaultAccess } from '../types';

const { Text } = Typography;

const EyeIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
    <path d="M2 12s3.5-6 10-6 10 6 10 6-3.5 6-10 6-10-6-10-6z" /><circle cx="12" cy="12" r="3" />
  </svg>
);
const PencilIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 3a2.83 2.83 0 0 1 4 4L7.5 20.5 3 21l.5-4.5z" />
  </svg>
);
const UploadIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <path d="M17 8l-5-5-5 5M12 3v13" />
  </svg>
);

/** The file's own extension, lower-case and without the dot. */
const extensionOf = (name: string) => {
  const i = (name || '').lastIndexOf('.');
  return i > -1 ? name.slice(i + 1).toLowerCase() : '';
};

interface Props {
  file: VaultFile;
  access: VaultAccess;
  onClose: () => void;
}

export function VaultFileViewer({ file, access, onClose }: Props) {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [replacing, setReplacing] = useState(false);
  const [meta, setMeta] = useState<VaultFileContent | null>(null);
  /* Why the window has nothing to show. A file whose bytes are gone is a
     different situation from one that failed to load, and a person can only act
     on the first if we say which it is instead of leaving a spinner up. */
  const [loadError, setLoadError] = useState<{ contentGone: boolean; message: string } | null>(null);
  const [draft, setDraft] = useState('');
  const [dirty, setDirty] = useState(false);
  /* A designed deck is edited ON its design, which is not a text draft — the
     component owns its own content and saves itself. `null` means this file has
     no design, so the plain slide editor takes over. */
  const designRef = useRef<DeckDesignHandle>(null);
  /* The file picker lives outside the layout so the button that opens it can
     sit in the footer, where it is visible whatever the pane is scrolled to. */
  const pickerRef = useRef<HTMLInputElement>(null);
  const [deckHasDesign, setDeckHasDesign] = useState(true);
  /* The View tab draws the REAL file — the .docx/.pptx laid out by the
     document engine, the same pages the agent's panel shows. This turns
     true only when the server says this particular file has no page
     preview (an old format, no engine), and the older drawing takes over.
     Reset per file: the window is reused for the next one. */
  const [pagesUnavailable, setPagesUnavailable] = useState(false);
  // The eye opens this to read; the pencil opens it to change. Both open the
  // same window — the difference is only which tab it starts on.
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [mode, setMode] = useState<'view' | 'edit'>(
    (file as { openInEditMode?: boolean }).openInEditMode && access.can_edit ? 'edit' : 'view',
  );

  const fileName = file.file_display_name || file.file_name || '';
  const previewKind = getPreviewKind(fileName, file.file_type);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    // Whether a deck has a design of ours is a fact about THIS file, and the
    // window is reused for the next one rather than remounted. Left standing,
    // one uploaded deck would send every designed deck opened after it to the
    // plain sheet editor.
    setDeckHasDesign(true);
    setPagesUnavailable(false);
    vaultService
      .getFileContent(file.file_id)
      .then((m) => {
        if (cancelled) return;
        setMeta(m);
        setDraft(m.content ?? '');
        setDirty(false);
      })
      .catch((e) => {
        if (cancelled) return;
        const data = e?.response?.data || {};
        setLoadError({
          contentGone: data.reason === 'content_missing',
          message: data.error || t('vault.viewer.loadFailed'),
        });
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
    // Depend on the file ONLY: this loads a file, and only a different file is
    // a reason to load again.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file.file_id]);

  const canEdit = access.can_edit && !!meta?.editable;
  /* Two different questions, and conflating them is what hid the upload:
     `canEdit` asks whether OUR EDITOR can open this format; this asks whether
     the person may change the file at all. Uploading a newer version needs
     only the second — it is raw bytes, it never goes near an editor — and it
     is the ONLY way to update a PDF or an image, which are exactly the files
     `canEdit` is false for. So the control was absent precisely where it was
     the only thing available. */
  const canReplace = access.can_edit;

  const handleSave = async () => {
    if (!meta) return;
    setSaving(true);
    try {
      // A designed deck was edited on the design, so the words go back through
      // the renderer that made the file rather than as replacement content.
      if (designRef.current) {
        await designRef.current.save();
        message.success(t('vault.viewer.saved'));
        return;
      }
      await vaultService.putFileContent(file.file_id, draft);
      message.success(t('vault.viewer.saved'));
      setDirty(false);
    } catch (e: any) {
      message.error(e?.response?.data?.error || t('vault.viewer.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  /** Replace this file's content with a version edited outside the app.
   *
   *  The in-app editors are deliberately simple, so real formatting work
   *  happens in Word / Excel / PowerPoint. This closes the loop: the SAME file
   *  is updated, so every folder placement, agent link and share it already had
   *  still points at it. The type must match — the server refuses a mismatch,
   *  and the picker below only offers the right extension in the first place. */
  const handleReplace = async (picked: File) => {
    if (!picked) return;
    // Confirm first. This does not create a version beside the old one — it
    // becomes the file, and every agent, folder and share pointing at it
    // serves the new contents from here on. That is the intent, and it is
    // also exactly why it should not happen on a single mis-click in a file
    // picker.
    const go = await new Promise<boolean>((resolve) => {
      Modal.confirm({
        title: t('vault.viewer.replaceConfirmTitle'),
        content: t('vault.viewer.replaceConfirmBody', {
          incoming: picked.name, current: fileName,
        }),
        okText: t('vault.viewer.replaceConfirmOk'),
        okButtonProps: { danger: true },
        cancelText: t('common.cancel'),
        onOk: () => resolve(true),
        onCancel: () => resolve(false),
      });
    });
    if (!go) return;
    setReplacing(true);
    try {
      await vaultService.replaceFileContent(file.file_id, picked);
      message.success(t('vault.viewer.replaceDone', { name: picked.name }));
      // Re-read the file so the viewer shows the version that is now stored,
      // rather than the bytes this modal opened with.
      const fresh = await vaultService.getFileContent(file.file_id);
      setMeta(fresh);
      setDraft(fresh.content ?? '');
      setDirty(false);
    } catch (e: any) {
      message.error(e?.response?.data?.error || t('vault.viewer.replaceFailed'));
    } finally {
      setReplacing(false);
    }
  };

  const handleSetting = async (key: 'management_mode', value: string) => {
    if (!meta) return;
    try {
      await vaultService.updateFileSettings(file.file_id, { [key]: value } as any);
      setMeta({ ...meta, [key]: value } as VaultFileContent);
      message.success(t('vault.viewer.settingSaved'));
    } catch (e: any) {
      message.error(e?.response?.data?.error || t('vault.viewer.saveFailed'));
    }
  };

  /* Files above this never come back through the API at all: the platform
     refuses to return a response that big, which is why a 34 MB PDF failed to
     open while a 1 MB one beside it was fine. For those we ask for a
     short-lived link to storage and fetch the bytes straight from there. The
     number matches _RESPONSE_CEILING_BYTES on the server. */
  const STREAM_FROM_STORAGE_ABOVE_MB = 24;

  /** Download the original bytes, whatever their size. */
  const handleDownload = async () => {
    try {
      if ((file.file_size_mb || 0) >= STREAM_FROM_STORAGE_ABOVE_MB) {
        const { data } = await api.get(`/files/download-by-id/${file.file_id}?url=1`);
        if (data?.url) {
          const a = document.createElement('a');
          a.href = data.url;
          a.download = fileName;
          a.rel = 'noopener';
          document.body.appendChild(a);
          a.click();
          a.remove();
          return;
        }
      }
      const res = await api.get(`/files/download-by-id/${file.file_id}`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (e: any) {
      message.error(e?.response?.data?.error || t('vault.viewer.previewFailed'));
    }
  };

  /**
   * The file, on the surface that matches what it IS.
   *
   * Reading and writing use the same component — a workbook is a grid whether
   * or not you may change a cell, a document is a page, a deck is slides. The
   * only difference is `readOnly`. A viewer that fell back to a flat text dump
   * showed a person something that was not their file; and one built on a
   * separate read-only renderer would drift away from the editor over time.
   */
  const surface = (readOnly: boolean) => {
    if (!meta) return null;
    const change = (next: string) => { setDraft(next); setDirty(true); };
    const ext = fileName.split('.').pop()?.toLowerCase() || '';

    // Word documents and decks are shown as their real pages — fonts,
    // colours, letterhead, page breaks — instead of a text dump or bare
    // slide sheets (operator report 2026-09-10). Only in the View tab:
    // editing still happens on the text/design surfaces below.
    const paginated = meta.view_format === 'document' || meta.view_format === 'deck';
    if (readOnly && paginated && !pagesUnavailable) {
      return (
        <div className="vault-preview-pane">
          <ExactPagesView
            fileId={file.file_id}
            title={fileName}
            onUnavailable={() => setPagesUnavailable(true)}
          />
        </div>
      );
    }

    switch (meta.view_format) {
      case 'sheet':
        return meta.office_format === 'xlsx' || meta.office_format === 'xlsm' ? (
          <VaultSheetEditor value={draft} readOnly={readOnly} kind="xlsx" onChange={change} />
        ) : (
          <VaultSheetEditor
            value={draft}
            readOnly={readOnly}
            kind="csv"
            delimiter={ext === 'tsv' ? '\t' : ','}
            onChange={change}
          />
        );
      case 'deck':
        // A deck looks the same in both tabs because both draw the same thing:
        // the real design when the file has one, the slide sheets when it does
        // not. Editing happens ON that rendering — on the design where there
        // is one, on the sheets otherwise — never on a third surface invented
        // for the purpose.
        if (readOnly) {
          return <DeckPreview fileId={file.file_id} envelope={draft} title={fileName} />;
        }
        if (deckHasDesign) {
          return (
            <DeckDesignEditor
              ref={designRef}
              fileId={file.file_id}
              title={fileName}
              onDirtyChange={setDirty}
              onNoDesign={() => setDeckHasDesign(false)}
            />
          );
        }
        return <VaultDeckEditor value={draft} readOnly={readOnly} onChange={change} />;
      case 'document':
      case 'markdown':
        return (
          <VaultDocEditor
            value={draft}
            readOnly={readOnly}
            variant={meta.view_format === 'document' ? 'document' : 'markdown'}
            onChange={change}
          />
        );
      case 'text':
        return (
          <textarea
            dir="auto"
            className="vault-viewer-editor"
            value={draft}
            readOnly={readOnly}
            onChange={(e) => change(e.target.value)}
          />
        );
      default:
        // No text form — a PDF, an image, or something we cannot open. The
        // preview renderer works from the bytes themselves.
        return (
          <div className="vault-preview-pane">
            <FilePreviewContent
              fileId={file.file_id}
              fileName={fileName}
              fileType={file.file_type}
              active
              unsupportedAction={
                <Button size="small" onClick={handleDownload}>{t('vault.viewer.download')}</Button>
              }
            />
          </div>
        );
    }
  };

  const editPane = () => {
    // Two different reasons the Edit tab may be closed, and they deserve
    // different sentences: the file cannot be edited here, or this person
    // cannot edit this file.
    if (!access.can_edit) {
      return (
        <div className="vault-edit-soon">
          <EyeIcon />
          <h4>{t('vault.viewer.viewOnlyTitle')}</h4>
          <p>{t('vault.viewer.viewOnlyBody')}</p>
          <Button size="small" onClick={() => setMode('view')}>
            {t('vault.viewer.backToView')}
          </Button>
        </div>
      );
    }
    if (meta?.editable) {
      return (
        <>
          {meta.office_format && (
            <Alert
              type="info"
              showIcon
              // A standing note about how the tab works, not an event: it is
              // read once and then lives above the thing you came to use, so
              // it earns one line rather than a banner.
              className="vault-note"
              message={t(
                meta.office_format === 'docx'
                  ? 'vault.viewer.docxEditNote'
                  : meta.office_format === 'pptx'
                    // A designed deck is edited ON the design, so the note has
                    // to say how — clicking the slide is not a guessable move.
                    ? (deckHasDesign ? 'vault.viewer.deckDesignHint'
                                     : 'vault.viewer.pptxEditNote')
                    : 'vault.viewer.xlsxEditNote',
              )}
            />
          )}
          {surface(!canEdit)}
          {replaceBlock()}
        </>
      );
    }
    // Not editable in place — say why, honestly, instead of showing a dead box.
    // The upload route still works here, and for a PDF or an image it is the
    // ONLY way to update the file, so it matters most on exactly this branch.
    return (
      <>
        <div className="vault-edit-soon">
          <PencilIcon />
          <h4>{t('vault.viewer.editSoonTitle')}</h4>
          <p>{meta?.message || t('vault.viewer.editSoonBody')}</p>
          <Button size="small" onClick={() => setMode('view')}>
            {t('vault.viewer.backToView')}
          </Button>
        </div>
        {replaceBlock()}
      </>
    );
  };

  /** "Upload a newer version" — the way out of a simple editor.
   *
   *  Offered whenever the person may edit this file, whatever its type: the
   *  point is precisely to cover what the in-app editors cannot do. The picker
   *  is restricted to the file's own extension so the common mistake (grabbing
   *  the wrong download) is caught before an upload starts. */
  const replaceBlock = () => null;

  /** The picker itself — one per window, opened from the footer. */
  const replaceInput = () => {
    if (!canReplace) return null;
    const ext = extensionOf(fileName);
    return (
      <input
        ref={pickerRef}
        type="file"
        hidden
        // The common mistake is grabbing the wrong download, so the picker only
        // offers the file's own type rather than letting the server refuse it
        // after an upload has already run.
        accept={ext ? `.${ext}` : undefined}
        onChange={(e) => {
          const picked = e.target.files?.[0];
          // Clear the input so picking the SAME file again re-fires change.
          e.target.value = '';
          if (picked) void handleReplace(picked);
        }}
      />
    );
  };

  return (
    <Modal
      open
      centered
      onCancel={onClose}
      width={980}
      className="vault-file-modal"
      title={(
        <span className="vault-viewer-title">
          <span dir="auto">{fileName}</span>
          <VaultDriveCopyBadge file={file} showLabel />
        </span>
      )}
      footer={
        <Space>
          {/* No point offering to download bytes that are not there. */}
          {!loadError?.contentGone && (
            <Button onClick={handleDownload}>{t('vault.viewer.download')}</Button>
          )}
          {/* Next to Download on purpose: they are the two halves of one loop —
              take it away, change it in Word, bring it back. */}
          {canReplace && (
            <Tooltip title={t('vault.viewer.replaceBody', { ext: extensionOf(fileName) ? `.${extensionOf(fileName)}` : '' })}>
              <Button
                icon={<UploadIcon />}
                loading={replacing}
                onClick={() => pickerRef.current?.click()}
              >
                {t('vault.viewer.replaceTitle')}
              </Button>
            </Tooltip>
          )}
          <Button onClick={onClose}>{t('common.close')}</Button>
          {mode === 'edit' && canEdit && (
            <Button type="primary" loading={saving} disabled={!dirty} onClick={handleSave}>
              {t('vault.viewer.save')}
            </Button>
          )}
        </Space>
      }
    >
      {replaceInput()}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 48 }}>
          <Spin />
        </div>
      ) : loadError || !meta ? (
        /* Say what happened. A spinner that never resolves, or a toast that has
           already faded, leaves a person looking at an empty window with no
           idea whether to wait, retry, or delete the file. */
        <div className="vault-viewer-gone">
          <Alert
            type={loadError?.contentGone ? 'warning' : 'error'}
            showIcon
            message={loadError?.contentGone
              ? t('vault.viewer.contentGoneTitle')
              : t('vault.viewer.loadFailed')}
            description={loadError?.contentGone
              ? t('vault.viewer.contentGoneBody')
              : loadError?.message}
          />
        </div>
      ) : (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          {/* View / Edit switch — only for someone who may actually edit. A
              person with read access got a second tab whose only content was a
              note saying they could not use it. */}
          <div className="vault-viewer-modes">
            {access.can_edit && (
              <Segmented
                value={mode}
                onChange={(v) => setMode(v as 'view' | 'edit')}
                options={[
                  { value: 'view', label: <span className="vault-mode-opt"><EyeIcon /> {t('vault.viewer.modeView')}</span> },
                  { value: 'edit', label: <span className="vault-mode-opt"><PencilIcon /> {t('vault.viewer.modeEdit')}</span> },
                ]}
              />
            )}
            {/* Only when there is genuinely nothing to show. A file with a text
                form now renders on its own editor, so the old note fired for
                spreadsheets and decks that display perfectly well. */}
            {meta.view_format === null && previewKind === 'unsupported' && (
              <Text type="secondary" style={{ fontSize: 12 }}>{t('vault.viewer.noPreview')}</Text>
            )}
          </div>

          {/* Manager settings: management mode + approval mode.
              Only for a file that CAN be edited. On a PDF both of these govern
              an action that is impossible either way, and a control that
              changes nothing is worse than no control. */}
          {access.can_edit && meta.editable && (
            <Space wrap size="large" className="vault-viewer-meta">
              <Space size="small">
                <Text type="secondary">{t('vault.viewer.managementMode')}:</Text>
                <Select
                  size="small"
                  value={meta.management_mode}
                  style={{ width: 140 }}
                  getPopupContainer={(trigger) => trigger.parentElement as HTMLElement}
                  className="vault-gov-select"
                  onChange={(v) => handleSetting('management_mode', v)}
                  options={[
                    { value: 'managed', label: t('vault.viewer.modeManaged') },
                    { value: 'locked', label: t('vault.viewer.modeLocked') },
                  ]}
                />
              </Space>
              {/* The per-file "Agent edits" switch is gone (operator decision
                  2026-09-07): regular agents are hard-coded write-never, and the
                  meta-agent's edits are approval cards under the user's own
                  Library permissions — so the switch only ever made the
                  authorized path refuse. */}
            </Space>
          )}

          {mode === 'view' ? (
            surface(true)
          ) : (
            editPane()
          )}
        </Space>
      )}
      {meta?.summary && (
        <VaultSummarySheet
          titleKey="vault.fileSummaryTitle"
          folderName={fileName}
          summary={meta.summary}
          updatedAt={null}
          open={summaryOpen}
          onToggle={setSummaryOpen}
        />
      )}
    </Modal>
  );
}
